import * as React from 'react';

import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {SearchBar} from 'shells/search-bar/search-bar';

import {NutshellFieldPicker} from '../../../../../core/nutshell-fields';
import {filterListItems} from '../../../../../core/nutshell-fields/helpers';
import type {FormFieldSchema, FormSchemaProperty} from '../../../../../core/nutshell-fields/types';
import type {FormFieldRowForDuplicateHelpers, View, SelectFieldInput} from '../../../../types';

import {isDisabled, getFilteredFields} from './helpers';

import styles from './other-fields.module.css';

type Props = {
    view: View;
    formSchema: FormFieldSchema;
    fields: FormFieldRowForDuplicateHelpers[];
    onSelectInputField: (arg1: SelectFieldInput) => void;
    setIsCreatingField: (name: string) => void;
};

export function OtherFields(props: Props) {
    const [customFieldsSearchValue, setCustomFieldsSearchValue] = React.useState<string>('');

    const formSchemaFields = Object.values(props.formSchema.properties);
    const shortTextFields = getFilteredFields({
        schemaFields: formSchemaFields,
        fields: props.fields,
        view: 'short-text',
    });
    const longTextFields = getFilteredFields({
        schemaFields: formSchemaFields,
        fields: props.fields,
        view: 'long-text',
    });
    const decisionFields = getFilteredFields({
        schemaFields: formSchemaFields,
        fields: props.fields,
        view: 'dropdown',
    });
    const multiChoiceFields = getFilteredFields({
        schemaFields: formSchemaFields,
        fields: props.fields,
        view: 'checkbox',
    });
    const dateFields = getFilteredFields({
        schemaFields: formSchemaFields,
        fields: props.fields,
        view: 'date',
    });

    const getPlaceholder = (view: View) => {
        switch (view) {
            case 'long-text':
                return 'Search long text fields…';
            case 'dropdown':
            case 'radio':
                return 'Search decision fields…';
            case 'checkbox':
                return 'Search multiple choice decision fields…';
            case 'date':
                return 'Search date fields…';
            case 'short-text':
            default:
                return 'Search short text fields…';
        }
    };

    const setIsCreatingField = () => {
        props.setIsCreatingField(customFieldsSearchValue);
    };

    return (
        <>
            <div className={styles.search}>
                <SearchBar
                    value={customFieldsSearchValue}
                    onChange={(val) => setCustomFieldsSearchValue(val)}
                    isClearable={true}
                    hasBorder={true}
                    placeholder={getPlaceholder(props.view)}
                    // @ts-expect-error
                    autofocus={true}
                />
            </div>
            <div className={styles.container}>
                <div className={styles['create-button-container']}>
                    <ButtonMover size='big' direction='left'>
                        <Button variant='text-primary' size='big' onClick={setIsCreatingField}>
                            + Create new custom field
                        </Button>
                    </ButtonMover>
                </div>
                <NutshellFieldPicker
                    onSelectField={(field: FormSchemaProperty) =>
                        props.onSelectInputField({
                            fieldKey: field.name,
                            // radio fields are also dropdown fields, and without specification are added as dropdowns
                            displayType: props.view === 'radio' ? props.view : field.displayType,
                            entityType: field.entityType,
                        })
                    }
                    customFilterFunction={(schemaFields: FormSchemaProperty[]) =>
                        getFilteredFields({schemaFields, fields: props.fields, view: props.view})
                    }
                    getIsItemDisabled={(field: FormSchemaProperty) =>
                        isDisabled(props.fields, field)
                    }
                    getDisabledText={() => 'This field is already in your form'}
                    searchValue={customFieldsSearchValue}
                    customSpecialStates={[
                        {
                            shouldRender: () =>
                                props.view === 'short-text' && shortTextFields.length === 0,
                            component: (
                                <p className={styles['empty-state']}>
                                    <i>
                                        You haven’t created any short text custom fields yet. Create
                                        a new custom field to get started.
                                    </i>
                                </p>
                            ),
                        },
                        {
                            shouldRender: () =>
                                props.view === 'long-text' && longTextFields.length === 0,
                            component: (
                                <p className={styles['empty-state']}>
                                    <i>
                                        You haven’t created any long text custom fields yet. Create
                                        a new custom field to get started.
                                    </i>
                                </p>
                            ),
                        },
                        {
                            shouldRender: () =>
                                (props.view === 'dropdown' || props.view === 'radio') &&
                                decisionFields.length === 0,
                            component: (
                                <p className={styles['empty-state']}>
                                    <i>
                                        You haven’t created any decision custom fields yet. Create a
                                        new custom field to get started.
                                    </i>
                                </p>
                            ),
                        },
                        {
                            shouldRender: () =>
                                props.view === 'checkbox' && multiChoiceFields.length === 0,
                            component: (
                                <p className={styles['empty-state']}>
                                    <i>
                                        You haven’t created any multiple choice decision custom
                                        fields yet. Create a new custom field to get started.
                                    </i>
                                </p>
                            ),
                        },
                        {
                            shouldRender: () => props.view === 'date' && dateFields.length === 0,
                            component: (
                                <p className={styles['empty-state']}>
                                    <i>
                                        You haven’t created any date custom fields yet. Create a new
                                        custom field to get started.
                                    </i>
                                </p>
                            ),
                        },
                        {
                            shouldRender: () =>
                                (props.view === 'short-text' &&
                                    filterListItems(shortTextFields, customFieldsSearchValue)
                                        .length === 0) ||
                                (props.view === 'long-text' &&
                                    filterListItems(longTextFields, customFieldsSearchValue)
                                        .length === 0) ||
                                ((props.view === 'dropdown' || props.view === 'radio') &&
                                    filterListItems(decisionFields, customFieldsSearchValue)
                                        .length === 0) ||
                                (props.view === 'date' &&
                                    filterListItems(dateFields, customFieldsSearchValue).length ===
                                        0) ||
                                (props.view === 'checkbox' &&
                                    filterListItems(multiChoiceFields, customFieldsSearchValue)
                                        .length === 0),

                            component: (
                                <NothingFoundEmptyState
                                    title='No fields were found'
                                    subtitle='Try loosening your search!'
                                />
                            ),
                        },
                    ]}
                />
            </div>
        </>
    );
}
